import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode, faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { CharlieButton } from '@ver.id/libs.ui.charlie';
import { HoruzProtocolChulakServiceSessionGuardTags } from '@ver.id/libs.internal.horuz';
import { useTranslation } from '../../i18n/i18n';
import { useUserAgent } from '../../hooks/useUserAgent';
import { type AppInfo, type RequestType } from '../../types';
import MobileLayout from '../../layouts/MobileLayout';
import FlowFooter from '../../components/FlowFooter';
import FaqModal, { type FaqTopic } from '../../components/FaqModal';
import QrCodeCard from '../../components/QrCodeCard';

type Props = {
  tags: HoruzProtocolChulakServiceSessionGuardTags;
  app?: AppInfo;
  requestType?: RequestType;
  onCancel: () => void;
  onBack?: () => void;
};

export default function MobileAgentStartPage({
  tags,
  app,
  requestType,
  onCancel,
  onBack,
}: Props) {
  const { t } = useTranslation();
  const userAgent = useUserAgent();
  const appName = app?.nameWithArticle || app?.name || 'your identity app';
  const isIssuance = requestType === 'issuance';
  const [showQrFallback, setShowQrFallback] = useState(false);
  const [faqTopic, setFaqTopic] = useState<FaqTopic | null>(null);

  const appLink = tags.app
    ? userAgent === 'ios'
      ? tags.app.universalAppLink || tags.app.deepAppLink
      : tags.app.intentAppLink || tags.app.deepAppLink
    : undefined;

  const secondaryLabel = onBack ? t('abort.goBack') : t('agentStart.cancel');
  const onSecondary = onBack ?? onCancel;

  // Build footer based on tag type
  let footer;
  if (tags.type === 'Redirect') {
    const redirectUri = (tags.redirect as { uri: string }).uri;
    footer = (
      <FlowFooter
        primaryLabel={t('agentStart.redirectButton')}
        onPrimary={() => window.location.replace(redirectUri)}
        secondaryLabel={secondaryLabel}
        onSecondary={onSecondary}
      />
    );
  } else if (appLink) {
    footer = (
      <FlowFooter
        primaryLabel={t('agentStart.openApp', { appName })}
        onPrimary={() => window.location.replace(appLink)}
        secondaryLabel={secondaryLabel}
        onSecondary={onSecondary}
      />
    );
  } else {
    footer = (
      <FlowFooter secondaryLabel={secondaryLabel} onSecondary={onSecondary} />
    );
  }

  return (
    <MobileLayout footer={footer}>
      {tags.type === 'Redirect' ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-6 text-center w-full">
          <div>
            <h2 className="text-2xl font-semibold text-foreground">
              {t('agentStart.title', { appName })}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t(
                isIssuance
                  ? 'agentStart.issuanceRedirectSubtitle'
                  : 'agentStart.redirectSubtitle',
                { appName },
              )}
            </p>
          </div>
          <FaqModal
            topic={faqTopic}
            onChangeTopic={setFaqTopic}
            onClose={() => setFaqTopic(null)}
          />
        </div>
      ) : (
        <div className="flex flex-1 flex-col w-full">
          <div className="flex flex-col gap-8 py-2">
            <div className="flex flex-col gap-2">
              <h2 className="text-2xl font-semibold text-foreground">
                {t('agentStart.title', { appName })}
              </h2>
              <p className="text-sm text-muted-foreground">
                {showQrFallback
                  ? t(
                      isIssuance
                        ? 'agentStart.issuanceSubtitle'
                        : 'agentStart.subtitle',
                      { appName },
                    )
                  : t(
                      isIssuance
                        ? 'agentStart.issuanceMobileSubtitle'
                        : 'agentStart.mobileSubtitle',
                      { appName },
                    )}
              </p>
            </div>

            {showQrFallback ? (
              <QrCodeCard
                qrData={tags.type === 'Qr' ? tags.qr.content : ''}
                appLogo={app?.logo}
                appName={appName}
              />
            ) : (
              <div className="flex flex-col items-center gap-8 rounded-2xl border border-border bg-background p-8">
                <a className="block p-4" href={appLink}>
                  <div className="relative flex items-center justify-center rounded-[26px] bg-muted p-4 size-[121px]">
                    {app?.logo && (
                      <img
                        src={app.logo}
                        alt={appName}
                        className="max-w-full max-h-full object-contain"
                      />
                    )}
                    <div className="absolute -bottom-1 -right-1 flex items-center justify-center rounded-full bg-[#a80a2d] p-[3px]">
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="size-3.5 text-white"
                      />
                    </div>
                  </div>
                </a>
                <CharlieButton
                  variant="outline"
                  pill
                  block
                  onClick={() => setShowQrFallback(true)}
                >
                  <FontAwesomeIcon icon={faQrcode} className="size-4" />
                  {t('agentStart.showQr')}
                </CharlieButton>
              </div>
            )}
          </div>
          <FaqModal
            topic={faqTopic}
            onChangeTopic={setFaqTopic}
            onClose={() => setFaqTopic(null)}
          />
        </div>
      )}
    </MobileLayout>
  );
}
